import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { User, Building2, MapPin, Phone, Loader2, Save } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function ProfilePage() {
  const { user, token, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    user_type: 'buyer',
    company_name: '',
    phone: '',
    location: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        user_type: user.user_type || 'buyer',
        company_name: user.company_name || '',
        phone: user.phone || '',
        location: user.location || ''
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.put(
        `${API}/auth/profile`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateUser(response.data);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="profile-page">
      <div className="max-w-2xl mx-auto px-6 md:px-12">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2">Settings</p>
          <h1 className="font-playfair text-3xl md:text-4xl font-bold text-foreground">
            Your Profile
          </h1>
        </div>

        {/* Profile Card */}
        <div className="p-6 rounded-lg bg-card/50 border border-border/20 mb-8">
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
                <span className="text-2xl font-bold text-white">{user?.name?.charAt(0)}</span>
              </div>
            )}
            <div>
              <h2 className="font-semibold text-lg text-foreground">{user?.name}</h2>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
              <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                user?.user_type === 'supplier' 
                  ? 'bg-accent/20 text-accent' 
                  : 'bg-primary/20 text-primary'
              }`}>
                {user?.user_type === 'supplier' ? 'Supplier' : 'Buyer'}
              </span>
            </div>
          </div>
        </div>

        {/* Edit Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="p-6 rounded-lg bg-card/50 border border-border/20 space-y-6">
            <h3 className="font-playfair text-xl font-semibold">Personal Information</h3>

            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Full Name</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full h-12 pl-12 pr-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground"
                  data-testid="profile-name-input"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Account Type</label>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, user_type: 'buyer' })}
                  className={`flex-1 p-4 rounded-lg border transition-all ${
                    formData.user_type === 'buyer'
                      ? 'border-accent/50 bg-accent/10 text-accent'
                      : 'border-border/40 text-muted-foreground'
                  }`}
                >
                  <User className="w-5 h-5 mx-auto mb-2" />
                  <p className="text-sm font-medium">Buyer</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, user_type: 'supplier' })}
                  className={`flex-1 p-4 rounded-lg border transition-all ${
                    formData.user_type === 'supplier'
                      ? 'border-accent/50 bg-accent/10 text-accent'
                      : 'border-border/40 text-muted-foreground'
                  }`}
                >
                  <Building2 className="w-5 h-5 mx-auto mb-2" />
                  <p className="text-sm font-medium">Supplier</p>
                </button>
              </div>
            </div>

            {formData.user_type === 'supplier' && (
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Company Name</label>
                <div className="relative">
                  <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="w-full h-12 pl-12 pr-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground"
                    placeholder="Your company name"
                    data-testid="profile-company-input"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Phone</label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full h-12 pl-12 pr-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground"
                  placeholder="+1 (555) 123-4567"
                  data-testid="profile-phone-input"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Location</label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full h-12 pl-12 pr-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground"
                  placeholder="City, Country"
                  data-testid="profile-location-input"
                />
              </div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 btn-gold"
            data-testid="save-profile-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </form>

        <ChangePasswordSection />
      </div>
    </div>
  );
}

function ChangePasswordSection() {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    if (form.new_password.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success('Password updated. Use it next time you sign in.');
      setForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="bg-card border border-border/40 rounded-xl p-6 mt-8 space-y-4"
      data-testid="change-password-form"
    >
      <div>
        <h3 className="font-playfair text-xl font-semibold mb-1">Change password</h3>
        <p className="text-xs text-muted-foreground">
          Pick a passphrase you don't reuse anywhere else. Minimum 8 characters.
        </p>
      </div>
      <input
        type="password"
        autoComplete="current-password"
        placeholder="Current password"
        value={form.current_password}
        onChange={(e) => setForm({ ...form, current_password: e.target.value })}
        className="w-full px-4 py-3 bg-secondary border border-border/40 rounded-lg focus:outline-none focus:border-accent text-foreground"
        data-testid="current-password-input"
        required
      />
      <input
        type="password"
        autoComplete="new-password"
        placeholder="New password (min 8 characters)"
        value={form.new_password}
        onChange={(e) => setForm({ ...form, new_password: e.target.value })}
        className="w-full px-4 py-3 bg-secondary border border-border/40 rounded-lg focus:outline-none focus:border-accent text-foreground"
        data-testid="new-password-input"
        required
        minLength={8}
      />
      <input
        type="password"
        autoComplete="new-password"
        placeholder="Confirm new password"
        value={form.confirm}
        onChange={(e) => setForm({ ...form, confirm: e.target.value })}
        className="w-full px-4 py-3 bg-secondary border border-border/40 rounded-lg focus:outline-none focus:border-accent text-foreground"
        data-testid="confirm-password-input"
        required
        minLength={8}
      />
      <Button
        type="submit"
        disabled={busy}
        className="bg-secondary hover:bg-secondary/80 border border-border/40"
        data-testid="change-password-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
        Update password
      </Button>
    </form>
  );
}
